/**
 * Design tokens for the web Rate It module, ported from the mobile app's
 * `src/screens/tiri/Rateit/constants.ts`.
 *
 * Kept as a copy rather than derived from anything in `lib/theme.js`, deliberately: these are
 * one module's identity and are shared with mobile, so they must move in step with THAT file
 * rather than with the CMS's own palette. If a tier gradient changes in Figma it changes in both
 * places or the two surfaces drift.
 *
 * Values that were `rs()`-scaled device points on mobile are plain pixels here — the web has no
 * equivalent responsive scale, and the layout adapts with CSS instead.
 */
export const RATE_IT_COLORS = {
  /** Ground behind every Rate It surface. */
  CARD_BG: '#0B0B0F',

  // --- The three tiers -----------------------------------------------------------------------
  // Two-stop diagonal gradients, light stop first, each with a matching lightened label colour.
  // These are the tile's RESTING look: all three are filled from the first frame, so the bar
  // reads as three coloured choices rather than three grey slabs the fan must tap to discover.
  // What selection changes is not the fill but the CONTRAST between tiles — see TILE_DIM_OPACITY.
  SOLID: ['#4A85D0', '#1F4372'],
  GREAT: ['#A93BE0', '#5B1B8A'],
  EPIC: ['#E8BE3E', '#9A6E12'],

  /** Label colour per tier — a lightened tint of that tier's own gradient, so the word and the
   * tile read as one object. The glyph above it stays white, which is what holds the three
   * icons together as one set. */
  SOLID_LABEL: '#A6CBF5',
  GREAT_LABEL: '#E2AEF9',
  EPIC_LABEL: '#F8E2A2',

  /** The bar's tinted slab over the media. */
  BAR_BG: 'rgba(10,10,12,0.72)',
  /**
   * The slab's ground on the feed card, now that the creator row sits ABOVE it. Darker than
   * `BAR_BG` for the same reason as mobile's `SLAB_BG`: with the creator row and its scrim above
   * the slab, the tiles lost the depth the stacked scrim used to give them.
   */
  SLAB_BG: 'rgba(10,10,12,0.88)',
  /** Ground beneath a tile's gradient — a backstop for the frame before the gradient paints. */
  TILE_BASE_BG: 'rgba(255,255,255,0.06)',
  TILE_BORDER: 'rgba(255,255,255,0.14)',
  /** Ring on the tile the fan picked. */
  TILE_SELECTED_BORDER: 'rgba(255,255,255,0.92)',
  CAPTION: 'rgba(255,255,255,0.72)',
  TILE_LABEL: '#FFFFFF'
};

/** How far the two unpicked tiles step back once a choice is made. */
export const TILE_DIM_OPACITY = 0.32;

/** Scrim behind the bar so the caption and creator pill stay readable over bright media. */
export const RATE_IT_SCRIM =
  'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.85) 100%)';

/**
 * The creator row's own scrim, drawn behind it (and the caption over it) and reaching
 * `RATE_IT_LAYOUT.CREATOR_SCRIM_REACH` above it. Mobile's `RATE_IT_CREATOR_SCRIM_*`: clear at the
 * top so it vanishes into the moment, ending near the slab's darkness so the row, the caption and
 * the tiles read as one surface.
 */
export const RATE_IT_CREATOR_SCRIM =
  'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 50%, rgba(5,5,5,0.8) 100%)';

/**
 * The creator row — a port of mobile's `TiriContentCreatorInfoOverlay`, which is itself FanCam's
 * player creator row at ~80% size. Same numbers as that file so the two can be diffed.
 */
export const TIRI_CREATOR = {
  /** Ringed avatar, the tallest thing in the row: 32 + a 2px ring each side. */
  AVATAR_SIZE: 36,
  AVATAR_RING: 2,
  AVATAR_RING_COLOR: 'rgba(93, 95, 239, 1)',
  /** Fallback disc behind the initials — mobile's `AvatarFallback` default (`theme.TALE`). */
  AVATAR_FALLBACK_BG: '#69EDE9',
  AVATAR_FALLBACK_TEXT: '#000000',
  ROW_GAP: 6,
  NAME_SIZE: 11,
  NAME_GAP: 5,
  /** FanCam's `PointsPill` (`small`): dark wash with a pink border, white "N pts". */
  PILL_BG: 'rgba(0, 0, 0, 0.4)',
  POINTS_BORDER: 'rgba(239, 93, 168, 0.5)',
  POINTS_SIZE: 10,
  VIEW_SIZE: 10,
  VIEW_ICON: 12
};

/**
 * Palette for the referral surfaces — the landing banner and the sign-up overlay.
 *
 * A deliberate duplicate of mobile's `RATE_IT_MILESTONE_COLORS`, which is itself a copy of the
 * FanCam referral modal's tokens — the same one deliberate duplication, carried across. Keeps
 * its name so the two repos stay greppable together.
 */
export const RATE_IT_MILESTONE_COLORS = {
  BG: '#2B1E4A',
  /** The primary CTA — the red "Invite Friends" button. */
  CTA: '#E11D48',
  /** Close buttons, message-box borders, other secondary surfaces. */
  SECONDARY: '#3A2E57',
  /** Copy that must recede: "NOT NOW", section labels. */
  MUTED: '#A6A6B3',
  WHITE: '#FFFFFF',
  SHEET_BG: '#221741'
};

export const RATE_IT_LAYOUT = {
  /** Minimum tap target, from the brief: 44px high, 80px wide. Floors, not design sizes. */
  TAP_FLOOR_HEIGHT: 44,
  TAP_FLOOR_WIDTH: 80,
  TILE_HEIGHT: 64,
  /**
   * The bar's height when it is a PICTURE of itself rather than a control — the strip above the
   * sign-up form. Short enough to read as a preview, and deliberately below the 44px tap floor:
   * the floor is a promise about things you can press, and this bar is inert.
   */
  TILE_COMPACT_HEIGHT: 34,
  TILE_RADIUS: 12,
  TILE_ICON_HEIGHT: 22,
  /** Gap between a tile's label and the per-tier counter just right of it. */
  TILE_COUNT_GAP: 4,
  TILE_GAP: 10,
  BAR_GUTTER: 12,
  BAR_PADDING_VERTICAL: 12,
  CAPTION_GAP: 8,
  /** Gap between the creator row and the tinted slab beneath it. */
  CREATOR_ROW_GAP: 10,
  /** How far the creator row's scrim reaches above the row, covering the caption over it too. */
  CREATOR_SCRIM_REACH: 120,
  /**
   * Above this width the feed stops being full-bleed and becomes a centred 9:16 stage on black.
   * A portrait moment stretched across a 21:9 desktop viewport reads as broken, not immersive.
   */
  DESKTOP_BREAKPOINT: 1024,
  /** The stage's max height on desktop; width follows from the 9:16 ratio. */
  STAGE_MAX_HEIGHT: 900,
  /**
   * Room the tiles leave below themselves on desktop for the pager's "next moment" chevron.
   *
   * 14 (the chevron's own offset from the bottom edge) + 22 (its height) + 8 of breathing room.
   * Keep it in step with `RateItPager`'s `Chevron` — the two are one layout agreement, and the
   * symptom of them drifting is the control sitting on the middle tile.
   */
  CHEVRON_CLEARANCE: 44
};

/**
 * The sign-up screen's own palette.
 *
 * Separate from `RATE_IT_MILESTONE_COLORS` because this screen is a FORM, not a celebration: it
 * needs a solid ground, a raised card and a field colour, none of which the referral surfaces
 * have. Sharing one grab-bag of purples between the two is how a form ends up tinted like a
 * modal.
 */
export const RATE_IT_AUTH_COLORS = {
  /** Behind everything — the strip the preview and the bar sit on. */
  GROUND: '#0C0A12',
  /** The form card raised off that ground. */
  CARD: '#17141F',
  FIELD: '#211D2B',
  FIELD_BORDER: 'rgba(255,255,255,0.10)',
  PLACEHOLDER: 'rgba(255,255,255,0.45)'
};

/**
 * The feed header — the close/TIRI row. (The creator row that used to sit beneath it now sits at
 * the bottom of the card, above the tiles — see `RateItCreatorRow`.)
 *
 * Mobile's `TIRI_HEADER`, carried across so the two headers can be diffed. The values that look
 * arbitrary are not: see `RateItHeader` for why the sponsor pill's height is pinned and why the
 * logo is sized by height alone.
 */
export const TIRI_HEADER = {
  /** Distance from the top edge to the row. */
  TOP_OFFSET: 10,
  /** Horizontal gutter for the row. */
  GUTTER: 12,
  /** Gap below the row to whatever sits under it (the referral banner). */
  ROW_GAP: 10,
  BUTTON_SIZE: 32,
  BUTTON_BG: 'rgba(0,0,0,0.4)',
  /** Glyph height inside the close circle. */
  BACK_ICON: 14,
  SPONSOR_HEIGHT: 26,
  SPONSOR_BORDER: 'rgba(255,255,255,0.18)',
  SPONSOR_LOGO_HEIGHT: 15,
  /** Gap between the TIRI wordmark and the p4p mark. */
  SPONSOR_GAP: 4
};

/**
 * Geometry for the milestone celebration — the full-bleed blur, the trophy badge, the headline.
 *
 * Mobile's `RATE_IT_MILESTONE_LAYOUT` minus everything that described its bottom sheet. The web
 * celebration carries no Invite Friends CTA and therefore has no sheet: it is the blur, the
 * badge, the headline and one way out. See `RateItMilestoneModal`.
 */
export const RATE_IT_MILESTONE_LAYOUT = {
  /**
   * How heavily the moment behind is blurred.
   *
   * A blur rather than a flat scrim because the brief spells the dismiss behaviour out as
   * "Remove blur" — the moment stays present and recognisable underneath, which is what makes
   * the celebration read as having happened ON this moment rather than in a dialog.
   */
  BLUR_PX: 18,
  /**
   * Tint over the blur, so white display type holds against a bright frame. Light on purpose:
   * the blur already separates the text, and anything heavier stops the moment reading as
   * present underneath, which is the thing the blur exists to preserve.
   */
  SCRIM: 'rgba(8,6,16,0.35)',
  /**
   * Rendered size of the badge. The only badge number here — the disc, its ring and the
   * trophy's proportions all live inside the asset's own 100x100 viewBox.
   */
  BADGE_SIZE: 96,
  /** Display type. Two short lines in the design, so it wraps rather than shrinking. */
  TITLE_SIZE: 30,
  TITLE_LINE_HEIGHT: 36,
  TITLE_GAP: 24
};

/**
 * How long a PHOTO moment stays up with no interaction before the feed moves on — the app's Rate
 * It and Tag It idle window. Videos don't use this; they advance when the clip ends. See
 * `RateItFeed`'s idle auto-advance.
 */
export const RATE_IT_PHOTO_AUTO_ADVANCE_MS = 15000;

/**
 * The cycle length, used ONLY as a fallback if a submit response omits `milestoneCount`.
 *
 * The server is authoritative — it sends the threshold it actually counted to, from
 * `constants/tiri.ts`. This exists so a missing field cannot render "You rated 0 moments!", and
 * mirrors mobile's `DEFAULT_MILESTONE_COUNT` for the same reason.
 */
export const RATE_IT_DEFAULT_MILESTONE_COUNT = 5;

/** How long the tier confirmation toast stays up. Each change of pick re-raises it. */
export const RATE_IT_TOAST_MS = 1200;

/**
 * The three tiers in display order, ascending.
 *
 * Pure data, mapped by the bar — so the tile order can never fall out of step with the tiers
 * themselves, and a fourth tier would be a row here rather than a new component.
 */
export const RATE_IT_TIERS = [
  {
    id: 'solid',
    label: 'Solid',
    gradient: RATE_IT_COLORS.SOLID,
    labelColor: RATE_IT_COLORS.SOLID_LABEL
  },
  {
    id: 'great',
    label: 'Great',
    gradient: RATE_IT_COLORS.GREAT,
    labelColor: RATE_IT_COLORS.GREAT_LABEL
  },
  {
    id: 'epic',
    label: 'Epic',
    gradient: RATE_IT_COLORS.EPIC,
    labelColor: RATE_IT_COLORS.EPIC_LABEL
  }
];

/**
 * What each side of a referral earns, for the first paint only.
 *
 * Matches the server's own default for `tiri.rateit.referralPoints`. The real figure arrives
 * with the deep link's `referral.points`, but the banner must render before that resolves — and
 * a blank or a zero in "you both earn N points" is worse than a number that is right in every
 * default deployment.
 */
export const RATE_IT_DEFAULT_REFERRAL_POINTS = 500;

/** Marks a minted link as a Rate It referral. Mirrors the backend's `RATE_IT_REFERRAL_TYPE`. */
export const RATE_IT_REFERRAL_TYPE = 'rateit';
